import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ReassuranceSection from '@/components/feature/ReassuranceSection';

const guides = [
  {
    to: '/guias/toldo-no-recoge',
    icon: 'ri-arrow-go-back-line',
    title: 'Mi toldo no recoge: causas y solución',
    description: '¿Tu toldo eléctrico no se enrolla? Descubre las causas más comunes y cómo solucionarlo. Problemas de motor, mando, freno o brazos atascados.',
    image: '/images/014-guia-index-no-recoge.webp'
  },
  {
    to: '/guias/brazo-toldo-roto',
    icon: 'ri-tools-line',
    title: 'Brazo de toldo roto: reparación',
    description: 'Brazos de punto recto o articulado dañados. Cómo identificar el tipo de brazo, si se puede reparar o hay que sustituirlo, y qué piezas suelen fallar.',
    image: '/images/018-guia-index-brazo-roto.webp'
  },
  {
    to: '/guias/cambiar-lona-toldo',
    icon: 'ri-paint-brush-line',
    title: 'Cambiar la lona o tela de un toldo',
    description: '¿Cuándo cambiar la lona y cuándo repararla? Tipos de tela, costes orientativos y cómo elegir la mejor lona para el clima de Málaga y la Costa del Sol.',
    image: '/images/022-guia-index-cambiar-lona.webp'
  },
  {
    to: '/guias/toldo-descolgado',
    icon: 'ri-contrast-drop-line',
    title: 'Toldo descolgado o descosido: cómo arreglarlo',
    description: 'Un toldo que cuelga o está descosido puede ser peligroso. Causas más frecuentes, riesgos si no se repara a tiempo y cómo actuar en Málaga con el viento de levante.',
    image: '/images/028-guia-index-descolgado.webp'
  },
  {
    to: '/guias/cambiar-motor-toldo',
    icon: 'ri-plug-line',
    title: 'Cambiar el motor de un toldo eléctrico',
    description: 'Todo sobre el motor de tu toldo: cuándo repararlo y cuándo sustituirlo, tipos de motores, compatibilidad y costes. Instalación de sensores de viento y sol.',
    image: '/images/019-guia-index-cambiar-motor.webp'
  },
];

export default function GuiasIndexPage() {
  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Guías de reparación de toldos en Málaga',
    description: 'Guías prácticas para diagnosticar y solucionar las averías más comunes en toldos. Reparación de brazos, cambio de lonas, motorización y más. Servicio profesional en Málaga.',
    url: 'https://toldos-malaga.es/guias',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: guides.map((guide, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://toldos-malaga.es${guide.to}`,
        name: guide.title,
      })),
    },
  };

  return (
    <>
      <title>Guías de Reparación de Toldos en Málaga | ToldosMálaga</title>
      <meta name="description" content="Guías prácticas para reparar tu toldo en Málaga. Averías frecuentes: toldo que no recoge, brazo roto, lona descosida, motor estropeado. Diagnóstico y soluciones con servicio local." />
      <meta name="keywords" content="guías reparación toldos, arreglar toldo Málaga, reparar toldo guía, toldo no recoge, brazo toldo roto, cambiar lona toldo" />
      <link rel="canonical" href="https://toldos-malaga.es/guias" />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://toldos-malaga.es/' },
              { '@type': 'ListItem', position: 2, name: 'Guías', item: 'https://toldos-malaga.es/guias' },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionSchema) }}
      />
      <meta property="og:title" content="Guías de Reparación de Toldos en Málaga | Toldos Málaga" />
      <meta property="og:description" content="Guías prácticas para reparar tu toldo en Málaga. Averías frecuentes: toldo que no recoge, brazo roto, lona descosida, motor estropeado. Diagnóstico y soluciones con servicio local." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://toldos-malaga.es/guias" />
      <meta property="og:image" content="https://toldos-malaga.es/og-image.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Toldos Málaga" />
      <meta property="og:locale" content="es_ES" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Guías de Reparación de Toldos en Málaga | Toldos Málaga" />
      <meta name="twitter:description" content="Guías prácticas para reparar tu toldo. Toldo no recoge, brazo roto, cambiar lona, toldo descolgado, cambiar motor." />
      <meta name="twitter:image" content="https://toldos-malaga.es/og-image.jpg" />

      <Navbar />
      <main className="min-h-screen bg-background-50">
        <nav aria-label="Breadcrumb" className="w-full max-w-[1280px] mx-auto px-4 md:px-6 pt-28 pb-2">
          <ol className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500">
            <li><Link to="/" className="hover:text-primary-500 transition-colors duration-200">Inicio</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li className="text-foreground-700 font-medium">Guías</li>
          </ol>
        </nav>

        <ReassuranceSection />

        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <header className="mb-10">
            <span className="inline-flex px-3 py-1 rounded-full border border-primary-200/60 bg-primary-100/60 text-primary-700 text-xs font-semibold font-label tracking-wide uppercase whitespace-nowrap mb-4">
              Guías prácticas
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-4">
              Guías de reparación<br />de toldos en Málaga
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-2xl">
              Hemos preparado estas guías para ayudarte a diagnosticar las averías más comunes en toldos. Cada guía explica las causas, posibles soluciones caseras, cuándo es mejor llamar a un profesional y los precios orientativos en Málaga.
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {guides.map((guide) => (
              <Link
                key={guide.to}
                to={guide.to}
                className="group flex flex-col rounded-2xl overflow-hidden bg-white border border-background-200/70 hover:border-primary-300 hover:shadow-sm transition-all duration-300"
              >
                <div className="w-full h-48 md:h-52 overflow-hidden">
                  <img
                    src={guide.image}
                    alt={guide.title}
                    title={guide.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-5 flex items-start gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-200 transition-colors duration-200">
                    <i className={`${guide.icon} text-primary-600 text-lg`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-base font-heading font-bold text-foreground-900 mb-1.5 group-hover:text-primary-600 transition-colors duration-200">
                      {guide.title}
                    </h2>
                    <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                      {guide.description}
                    </p>
                    <span className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-primary-600 group-hover:text-primary-700 transition-colors whitespace-nowrap">
                      <span>Leer guía</span>
                      <i className="ri-arrow-right-line text-xs group-hover:translate-x-0.5 transition-transform"></i>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-12 p-6 md:p-8 bg-[#0F5066] rounded-2xl text-white">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="flex-1">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-white mb-2">
                  ¿No encuentras lo que buscas?
                </h2>
                <p className="text-white/70 text-sm leading-relaxed">
                  Si tu toldo tiene una avería que no aparece en estas guías, llámanos. Hacemos diagnóstico gratuito en menos de 24 horas en toda el área de Málaga.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto flex-shrink-0">
                <a
                  href="tel:+34951120770"
                  className="flex items-center justify-center gap-2 px-6 py-3.5 bg-white text-[#0F5066] rounded-full font-bold text-sm hover:bg-accent-100 transition-all duration-200 whitespace-nowrap"
                >
                  <i className="ri-phone-line text-xl"></i>
                  <span>951 12 07 70</span>
                </a>
                <a
                  href="https://wa.me/34627213347"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-6 py-3.5 bg-accent-500 text-foreground-900 rounded-full font-bold text-sm hover:bg-accent-400 transition-all duration-200 whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                  <span>627 21 33 47</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}